import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../services/api';
import './ApplicationDependencies.css';

const getTodayISO = () => new Date().toISOString().split('T')[0];

interface ResourceCapacityRecord {
  resource_pool_id: number;
  resource_type: string;
  cost_per_day: number;
  resource_location: 'On-Site' | 'Off-Shore';
  effective_start_date: string;
  effective_end_date: string;
  created_at?: string;
  updated_at?: string;
}

interface SkillsetMaster {
  skillset_id: string;
  skillset_name: string;
  skillset_description: string;
}

const ResourceCapacity: React.FC = () => {
  const [records, setRecords] = useState<ResourceCapacityRecord[]>([]);
  const [skillsets, setSkillsets] = useState<SkillsetMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [filterResourceType, setFilterResourceType] = useState<string>('');
  const [filterLocation, setFilterLocation] = useState<string>('');
  const [editingRecord, setEditingRecord] = useState<ResourceCapacityRecord | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [formData, setFormData] = useState({
    resourcePoolId: '',
    resourceType: '',
    costPerDay: '',
    resourceLocation: 'On-Site',
    effectiveStartDate: getTodayISO(),
    effectiveEndDate: '9999-12-31'
  });

  const fetchRecords = async () => {
    try {
      const response = await authFetch('/api/resource-capacity');
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      // Sort by Resource Pool ID in ascending order
      data.sort((a: ResourceCapacityRecord, b: ResourceCapacityRecord) => a.resource_pool_id - b.resource_pool_id);
      setRecords(data);
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load resource capacity records');
      setLoading(false);
    }
  };

  const fetchSkillsets = async () => {
    try {
      const response = await authFetch('/api/skillset-master');
      if (!response.ok) throw new Error('Failed to fetch skillsets');
      const result = await response.json();
      setSkillsets(result.data || []);
    } catch (err: any) {
      console.error('Failed to load skillsets:', err);
      setSkillsets([]);
    }
  };

  useEffect(() => {
    fetchRecords();
    fetchSkillsets();
  }, []);


  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFile(file);
    setShowUploadModal(true);
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim());
      
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
      
      setPreviewData(data);
    };
    
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    try {
      const response = await authFetch('/api/resource-capacity/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(previewData)
      });
      
      const data = await response.json();
      
      if (data.imported > 0) {
        setSuccess(`Successfully imported ${data.imported} resource capacity records`);
        fetchRecords();
      }
      
      if (data.errors && data.errors.length > 0) {
        const errorMessage = data.errors.join('\n');
        setError(`Import completed with errors:\n${errorMessage}`);
      }
      
      setShowUploadModal(false);
      setPreviewData([]);
      setUploadFile(null);
    } catch (err: any) {
      setError(err.message || 'Failed to import data');
    }

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = `Resource Type,Cost Per Day,Resource Location,Effective Start Date,Effective End Date
Cloud Architect,1200,On-Site,2025-01-01,9999-12-31
DevOps Engineer,950,Off-Shore,2025-01-01,9999-12-31
AWS Architect,1100,On-Site,2025-01-01,9999-12-31
Scrum Master,850,On-Site,2025-01-01,9999-12-31
QA Engineer,700,Off-Shore,2025-01-01,9999-12-31
Business Analyst,750,On-Site,2025-01-01,9999-12-31`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'resource_capacity_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const downloadData = () => {
    if (records.length === 0) {
      alert('No data to download');
      return;
    }

    // Match template format: Resource Type,Cost Per Day,Resource Location,Effective Start Date,Effective End Date
    const headers = ['Resource Type', 'Cost Per Day', 'Resource Location', 'Effective Start Date', 'Effective End Date'];
    const csvRows = [headers.join(',')];

    records.forEach(record => {
      const row = [
        `"${(record.resource_type || '').replace(/"/g, '""')}"`,
        record.cost_per_day || 0,
        `"${(record.resource_location || '').replace(/"/g, '""')}"`,
        record.effective_start_date || '2025-01-01',
        record.effective_end_date || '9999-12-31'
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `resource_capacity_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate that the resource type exists in skillsets
    if (formData.resourceType) {
      const skillsetExists = skillsets.some(skillset =>
        skillset.skillset_name.toLowerCase() === formData.resourceType.toLowerCase()
      );

      if (!skillsetExists) {
        setError(`Resource Skillset "${formData.resourceType}" does not exist in the Resource Skillsets table. Please add it first or select an existing one.`);
        return;
      }
    }

    // Validate date range
    if (formData.effectiveStartDate && formData.effectiveEndDate) {
      const startDate = new Date(formData.effectiveStartDate);
      const endDate = new Date(formData.effectiveEndDate);
      if (endDate < startDate) {
        setError('Effective End Date must be after Effective Start Date');
        return;
      }
    }

    try {
      const payload = {
        resource_pool_id: formData.resourcePoolId ? parseInt(formData.resourcePoolId) : null,
        resource_type: formData.resourceType,
        cost_per_day: formData.costPerDay ? parseFloat(formData.costPerDay) : 0,
        resource_location: formData.resourceLocation || 'On-Site',
        effective_start_date: formData.effectiveStartDate || getTodayISO(),
        effective_end_date: formData.effectiveEndDate || '9999-12-31'
      };

      if (editingRecord) {
        const response = await authFetch(`/api/resource-capacity/${editingRecord.resource_pool_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Failed to update');
        setSuccess('Resource capacity record updated successfully');
      } else {
        const response = await authFetch('/api/resource-capacity', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Failed to create');
        setSuccess('Resource capacity record created successfully');
      }

      fetchRecords();
      
      // Close modal and reset form after successful save
      setShowForm(false);
      setEditingRecord(null);
      resetFormData();
      
      // Clear any error messages
      setError('');
    } catch (err: any) {
      console.error('Failed to save resource capacity record:', err);
      setError(err.message || 'Failed to save resource capacity record');
    }
  };

  const resetFormData = () => {
    setFormData({
      resourcePoolId: '',
      resourceType: '',
      costPerDay: '',
      resourceLocation: 'On-Site',
      effectiveStartDate: getTodayISO(),
      effectiveEndDate: '9999-12-31'
    });
  };

  const handleEdit = (record: ResourceCapacityRecord) => {
    setEditingRecord(record);
    setViewMode(false);
    setFormData({
      resourcePoolId: record.resource_pool_id.toString(),
      resourceType: record.resource_type,
      costPerDay: record.cost_per_day?.toString() || '',
      resourceLocation: record.resource_location || 'On-Site',
      effectiveStartDate: record.effective_start_date || getTodayISO(),
      effectiveEndDate: record.effective_end_date || '9999-12-31'
    });
    setShowForm(true);
  };

  const handleView = (record: ResourceCapacityRecord) => {
    setEditingRecord(record);
    setViewMode(true);
    setFormData({
      resourcePoolId: record.resource_pool_id.toString(),
      resourceType: record.resource_type,
      costPerDay: record.cost_per_day?.toString() || '',
      resourceLocation: record.resource_location || 'On-Site',
      effectiveStartDate: record.effective_start_date || getTodayISO(),
      effectiveEndDate: record.effective_end_date || '9999-12-31'
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this resource capacity record?')) {
      try {
        const response = await authFetch(`/api/resource-capacity/${id}`, {
          method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to delete');
        setSuccess('Resource capacity record deleted successfully');
        fetchRecords();
      } catch (err: any) {
        setError(err.message || 'Failed to delete resource capacity record');
      }
    }
  };

  const handleAddNew = () => {
    setEditingRecord(null);
    resetFormData();
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingRecord(null);
    setViewMode(false);
    resetFormData();
    setError('');
  };


  if (loading) return <div className="md-loading">Loading resource capacity records...</div>;
  if (error && !records.length) return <div className="md-error">Error: {error}</div>;

  const filteredRecords = records.filter(record => {
    const typeMatch = filterResourceType === '' || 
      record.resource_type.toLowerCase().includes(filterResourceType.toLowerCase());
    const locationMatch = filterLocation === '' || 
      record.resource_location === filterLocation;
    return typeMatch && locationMatch;
  });

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Header - Fixed/Sticky */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Resource Capacity Management</h1>
            <p className="page-header__subtitle">
              Manage resource pools, capacity planning, and availability by resource type
            </p>
          </div>
          <div className="page-header__actions">
            <button
              className="btn btn-primary"
              onClick={handleAddNew}
              title="Create new resource capacity record"
            >
              ➕ Add Resource Pool
            </button>
            <button
              className="btn btn-outline-info"
              onClick={downloadTemplate}
              title="Download CSV template for bulk import"
            >
              📋 Download Template
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
                fileInputRef.current?.click();
              }}
              title="Import resource records from CSV"
            >
              📤 Upload File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            {records.length > 0 && (
              <button
                className="btn btn-outline-success"
                onClick={downloadData}
                title="Download current data as CSV"
                style={{ border: '2px solid #28a745' }}
              >
                📥 Download Data
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px' }}>

      {/* Filter Section */}
      {records.length > 0 && (
        <div className="md-card" style={{ marginBottom: '16px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '16px', fontWeight: '500', minWidth: '120px' }}>Filter by Type:</label>
            <input
              type="text"
              value={filterResourceType}
              onChange={(e) => setFilterResourceType(e.target.value)}
              placeholder="Enter resource type to filter..."
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                minWidth: '200px'
              }}
            />
            <label style={{ fontSize: '16px', fontWeight: '500', minWidth: '120px' }}>Filter by Location:</label>
            <select
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                minWidth: '150px'
              }}
            >
              <option value="">All Locations</option>
              <option value="On-Site">On-Site</option>
              <option value="Off-Shore">Off-Shore</option>
            </select>
            {(filterResourceType || filterLocation) && (
              <button
                onClick={() => {
                  setFilterResourceType('');
                  setFilterLocation('');
                }}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Clear Filters
              </button>
            )}
            <span style={{ fontSize: '16px', color: '#666' }}>
              Showing {filteredRecords.length} of {records.length} resource pools
            </span>
          </div>
        </div>
      )}

      {/* Main Content Card */}
      <div className="md-card" style={{ flex: '1', overflow: 'visible', minHeight: 'auto' }}>
        {records.length === 0 ? (
          <div className="md-empty-state" style={{ padding: '40px' }}>
            <div className="md-empty-icon">👥</div>
            <h3>No Resource Capacity Records Found</h3>
            <p>Use the action cards above to add resource pools or upload data from a CSV file</p>
          </div>
        ) : (
          <div className="md-table-container" style={{ overflowY: 'visible', height: 'auto', maxHeight: 'none' }}>
            <table className="md-table" style={{ fontSize: '16px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>Resource Skillset</th>
                  <th style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>Cost/Day</th>
                  <th style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>Resource Location</th>
                  <th style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>Effective Start Date</th>
                  <th style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>Effective End Date</th>
                  <th style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map(record => (
                  <tr key={record.resource_pool_id}>
                    <td style={{ textAlign: 'left', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>
                      {record.resource_type}
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>
                      ${record.cost_per_day || 0}
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>
                      {record.resource_location}
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>
                      {record.effective_start_date ? new Date(record.effective_start_date).toLocaleDateString() : '01/01/2025'}
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>
                      {record.effective_end_date ? new Date(record.effective_end_date).toLocaleDateString() : '12/31/9999'}
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                        <button
                          onClick={() => handleView(record)}
                          title="View details"
                          style={{
                            padding: '4px 6px',
                            backgroundColor: 'transparent',
                            color: '#1976d2',
                            border: '1px solid #1976d2',
                            borderRadius: '4px',
                            fontSize: '16px',
                            cursor: 'pointer',
                            minWidth: 'auto'
                          }}
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => handleEdit(record)}
                          title="Edit resource pool"
                          style={{
                            padding: '4px 6px',
                            backgroundColor: 'transparent',
                            color: '#1976d2',
                            border: '1px solid #1976d2',
                            borderRadius: '4px',
                            fontSize: '16px',
                            cursor: 'pointer',
                            minWidth: 'auto'
                          }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(record.resource_pool_id)}
                          title="Delete resource pool"
                          style={{
                            padding: '4px 6px',
                            backgroundColor: 'transparent',
                            color: '#d32f2f',
                            border: '1px solid #d32f2f',
                            borderRadius: '4px',
                            fontSize: '16px',
                            cursor: 'pointer',
                            minWidth: 'auto'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>{/* End Scrollable Content Area */}

      {/* Resource Capacity Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{margin: 0, fontSize: '24px', fontWeight: '400', color: '#212121'}}>
                {viewMode ? 'View Resource Pool' : editingRecord ? 'Edit Resource Pool' : 'Add New Resource Pool'}
              </h2>
              <button 
                onClick={handleCloseForm}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  background: 'transparent',
                  fontSize: '24px',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#757575'
                }}
              >
                ×
              </button>
            </div>
            <div style={{padding: '24px'}}>
              <form onSubmit={handleSubmit}>
                {error && (
                  <div className="md-error" style={{ marginBottom: '16px' }}>
                    {error}
                  </div>
                )}

                {editingRecord && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Resource Pool ID</label>
                    <input
                      type="text"
                      className="md-input"
                      value={formData.resourcePoolId}
                      disabled
                      style={{ backgroundColor: '#f5f5f5', width: '100%', padding: '10px 12px', fontSize: '16px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                )}

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Resource Skillset *</label>
                  <select
                    className="md-input"
                    value={formData.resourceType}
                    onChange={(e) => setFormData({...formData, resourceType: e.target.value})}
                    required
                    disabled={viewMode}
                    style={{ width: '100%', padding: '10px 12px', fontSize: '16px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: viewMode ? '#f5f5f5' : 'white' }}
                  >
                    <option value="">Select a skillset...</option>
                    {skillsets.map(skillset => (
                      <option key={skillset.skillset_id} value={skillset.skillset_name}>
                        {skillset.skillset_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Cost Per Day</label>
                  <input
                    type="text"
                    className="md-input"
                    value={formData.costPerDay}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setFormData({...formData, costPerDay: value});
                    }}
                    placeholder="0"
                    disabled={viewMode}
                    style={{ width: '100%', padding: '10px 12px', fontSize: '16px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: viewMode ? '#f5f5f5' : 'white' }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Resource Location *</label>
                  <select
                    className="md-input"
                    value={formData.resourceLocation}
                    onChange={(e) => setFormData({...formData, resourceLocation: e.target.value as 'On-Site' | 'Off-Shore'})}
                    required
                    disabled={viewMode}
                    style={{ width: '100%', padding: '10px 12px', fontSize: '16px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: viewMode ? '#f5f5f5' : 'white' }}
                  >
                    <option value="On-Site">On-Site</option>
                    <option value="Off-Shore">Off-Shore</option>
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Effective Start Date *</label>
                    <input
                      type="date"
                      className="md-input"
                      value={formData.effectiveStartDate}
                      onChange={(e) => setFormData({...formData, effectiveStartDate: e.target.value})}
                      required
                      disabled={viewMode}
                      style={{ width: '100%', padding: '10px 12px', fontSize: '16px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: viewMode ? '#f5f5f5' : 'white' }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Effective End Date *</label>
                    <input
                      type="date"
                      className="md-input"
                      value={formData.effectiveEndDate}
                      onChange={(e) => setFormData({...formData, effectiveEndDate: e.target.value})}
                      required
                      disabled={viewMode}
                      style={{ width: '100%', padding: '10px 12px', fontSize: '16px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: viewMode ? '#f5f5f5' : 'white' }}
                    />
                  </div>
                </div>

                <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '20px', borderTop: '1px solid #e0e0e0'}}>
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    style={{padding: '12px 24px', fontSize: '16px', fontWeight: '500', border: '1px solid #1976d2', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'transparent', color: '#1976d2'}}
                  >
                    {viewMode ? 'Close' : 'Cancel'}
                  </button>
                  {!viewMode && (
                    <button
                      type="submit"
                      style={{padding: '12px 24px', fontSize: '16px', fontWeight: '500', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#1976d2', color: 'white'}}
                    >
                      {editingRecord ? 'Update Resource Pool' : 'Create Resource Pool'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '800px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{margin: 0, fontSize: '20px', fontWeight: '400'}}>Preview Import Data</h2>
              <button 
                onClick={() => setShowUploadModal(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  background: 'transparent',
                  fontSize: '24px',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <div style={{padding: '24px'}}>
              {previewData.length > 0 ? (
                <div>
                  <p>Found {previewData.length} records. Preview:</p>
                  <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <table style={{ width: '100%', fontSize: '12px' }}>
                      <thead style={{ backgroundColor: '#f5f5f5' }}>
                        <tr>
                          {Object.keys(previewData[0] || {}).map(key => (
                            <th key={key} style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.slice(0, 5).map((row, index) => (
                          <tr key={index}>
                            {Object.values(row).map((value: any, cellIndex) => (
                              <td key={cellIndex} style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                {value}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {previewData.length > 5 && (
                    <p style={{ fontSize: '12px', color: '#666' }}>
                      ... and {previewData.length - 5} more records
                    </p>
                  )}
                </div>
              ) : (
                <p>No data found in the uploaded file.</p>
              )}
              
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px'}}>
                <button
                  onClick={() => setShowUploadModal(false)}
                  style={{padding: '12px 24px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'white'}}
                >
                  Cancel
                </button>
                {previewData.length > 0 && (
                  <button
                    onClick={handleBulkImport}
                    style={{padding: '12px 24px', fontSize: '14px', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#1976d2', color: 'white'}}
                  >
                    Import {previewData.length} Records
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Success/Error Messages */}
      {success && (
        <div className="md-toast md-toast--success">
          {success}
          <button onClick={() => setSuccess('')}>×</button>
        </div>
      )}
      {error && (
        <div className="md-toast md-toast--error">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}
    </div>
  );
};

export default ResourceCapacity;